import React from 'react'
import {
  Box,
  FormControlLabel,
  type FormControlLabelProps,
  Radio as MuiRadio,
  type RadioProps as MuiRadioProps,
  type SxProps,
  Typography,
} from '@mui/material'

import { spacing } from '~tokens/internal'

export interface RadioProps extends MuiRadioProps {
  label?: string
  labelPlacement?: FormControlLabelProps['labelPlacement']
  description?: string
  containerSx?: SxProps
}

const Radio = React.forwardRef<HTMLButtonElement, RadioProps>(
  ({ label, labelPlacement = 'end', description, containerSx, disabled, ...props }, ref) => {
    const radioElement = <MuiRadio ref={ref} disabled={disabled} {...props} />

    if (!label && !description) {
      return radioElement
    }

    if (description) {
      return (
        <Box
          component='label'
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: spacing.xxs,
            cursor: disabled ? 'default' : 'pointer',
            ...containerSx,
          }}
        >
          {radioElement}
          <Box sx={{ pt: spacing.xxs }}>
            {label && <Typography variant='subtitle1'>{label}</Typography>}
            <Typography variant='body2' color={disabled ? 'textDisabled' : 'textSecondary'}>
              {description}
            </Typography>
          </Box>
        </Box>
      )
    }

    return (
      <FormControlLabel
        control={radioElement}
        label={label}
        labelPlacement={labelPlacement}
        disabled={disabled}
        sx={{
          ...(labelPlacement === 'start' && {
            justifyContent: 'space-between',
          }),
          ...containerSx,
        }}
      />
    )
  },
)

Radio.displayName = 'Radio'

export default Radio
